import LabelWithAsterisk from '@/components/LabelWithAsterisk';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { CircleAlert, InfoIcon } from 'lucide-react';
import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { toast } from 'sonner';

interface _DescriptionDetails {
    description: string;
    setDescription: React.Dispatch<React.SetStateAction<string>>;
    headline: string;
    setHeadline: React.Dispatch<React.SetStateAction<string>>;
    markAsDuplicate?: boolean;
    edit?: boolean;
    /** Optional AI actions rendered beside the Description label. */
    aiSlot?: React.ReactNode;
}

export interface DescriptionDetailsRef {
    callback: () => boolean;
}

const DescriptionDetails = forwardRef<DescriptionDetailsRef, _DescriptionDetails>(({
    description,
    setDescription,
    headline,
    setHeadline,
    markAsDuplicate,
    aiSlot
}, ref) => {
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const BAD_WORDS = [
        // Malaysian / Singaporean terms
        'babi', 'celaka', 'bangang', 'sial', 'kimak', 'lanci', 'lancau', 'lanjiao', 'sohai', 'tahi', 
        'kitak', 'isap', 'gi amput', 'bengk', 'janik',
        // English terms
        'fuck', 'shit', 'asshole', 'bitch', 'bastard', 'cunt', 'dick', 'piss off', 
        'son of a bitch', 'bollocks', 'wanker'
    ];
    
    const checkForBadWords = (text: string) => {
        const lowerText = text.toLowerCase();
        return BAD_WORDS.filter(bad => lowerText.includes(bad));
    };
    
    const validateFields = (headlineVal: string, descriptionVal: string) => {
        const errors: string[] = [];
    
        // Headline validation - minimum 10 characters, maximum 20 words
        const headlineWordCount = headlineVal.trim().split(/\s+/).filter(Boolean).length;
        if (!headlineVal || headlineVal.trim().length < 10) {
            errors.push('headline');
        } else if (headlineWordCount > 20) {
            errors.push('headlineWordLimit');
        }
    
        // Description validation - minimum 20 words, maximum 200 words, max 2000 characters
        const wordCount = descriptionVal.trim().split(/\s+/).filter(Boolean).length;
        if (!descriptionVal || wordCount < 20) {
            errors.push('description');
        } else if (wordCount > 200) {
            errors.push('descriptionWordLimit');
        } else if (descriptionVal.length > 2000) {
            errors.push('descriptionCharLimit');
        }
    
        // Bad words check
        const headlineBad = checkForBadWords(headlineVal);
        const descriptionBad = checkForBadWords(descriptionVal);
    
        if (headlineBad.length) errors.push('headlineBad');
        if (descriptionBad.length) errors.push('descriptionBad');
    
        // Save state for displaying inline errors
        setValidationErrors(errors);
    
        return errors;
    };
    

    const callback = () => {
        const errors = validateFields(headline, description);

        if (errors.length > 0) {
            let message = 'Please complete all required fields.';
            toast.error(message);
            
            // Scroll to the first error field
            // Priority order: headline errors first, then description errors
            const errorPriority = ['headline', 'headlineWordLimit', 'headlineBad', 'description', 'descriptionWordLimit', 'descriptionCharLimit', 'descriptionBad'];
            const firstError = errorPriority.find(err => errors.includes(err));
            
            if (firstError) {
                let targetElement: HTMLElement | null = null;
                
                if (firstError.startsWith('headline')) {
                    targetElement = document.querySelector('textarea[placeholder*="impactful description"]') as HTMLElement;
                } else if (firstError.startsWith('description')) {
                    targetElement = document.querySelector('textarea[placeholder*="property\'s features"]') as HTMLElement;
                }
                
                if (targetElement) {
                    const elementTop = targetElement.getBoundingClientRect().top + window.scrollY;
                    const offset = 100; // Offset from top to give some breathing room
                    window.scrollTo({ top: elementTop - offset, behavior: 'smooth' });
                    
                    // Optional: Focus the field after scrolling
                    setTimeout(() => {
                        targetElement?.focus();
                    }, 500);
                }
            }
            
            return false;
        }
        return true;
    };

    useImperativeHandle(ref, () => ({
        callback
    }));

    useEffect(() => {
        validateFields(headline, description);
    }, [headline, description]);

    // Calculate current word counts
    const headlineWordCount = headline.trim().split(/\s+/).filter(Boolean).length;
    const descriptionWordCount = description.trim().split(/\s+/).filter(Boolean).length;

    return (
        <section>
            <Alert className="mt-3 mb-3 border-blue-300 bg-blue-50 flex flex-col">
                <div className="flex items-center mb-1">
                    <InfoIcon className="mr-2 h-4 w-4 text-blue-500" />
                    <AlertTitle>SEO Keyword Suggestions</AlertTitle>
                </div>
                <AlertDescription>
                    Consider including relevant keywords in your description to help your listing appear in search results.
                </AlertDescription>
                <div className="mt-2 flex flex-wrap gap-2">
                    {[
                        'apartment', 'condo', 'townhouse', 'penthouse', 
                        'swimming pool', 'gym', 'balcony', 'garden', 
                        'near MRT', 'city center', 'gated community', 'near school', 
                        'family-friendly', 'sea view', 'investment property', 'luxury', 'affordable'
                    ].map((keyword) => (
                        <span key={keyword} className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs cursor-pointer hover:bg-blue-200">
                            {keyword}
                        </span>
                    ))}
                </div>
            </Alert>
            <Alert className="mt-2 mb-3 border-red-300 bg-red-50 flex flex-col">
                <div className="flex items-center mb-1">
                    <CircleAlert className="mr-2 h-4 w-4 text-red-500" />
                    <AlertTitle>Content Policy Notice</AlertTitle>
                </div>
                <AlertDescription>
                    We reserve the right to take down your post if it contains any explicit content or foul language. 
                    Please ensure your listing is appropriate for all users.
                </AlertDescription>
            </Alert>
            <div className='mt-3 pt-5'>
                <LabelWithAsterisk label='Headline' className='text-black'/>
                <div>
                    <Textarea
                    className={`mt-3 ${(validationErrors.includes('headline') || validationErrors.includes('headlineWordLimit')) && 'border-red-400'}`}
                    value={headline}
                    placeholder='Enter a short, impactful description emphasizing the key selling points.'
                    onChange={(e) => setHeadline(e.target.value)}
                    disabled={markAsDuplicate}/>
                    <div className="flex justify-between items-center mt-1">
                        <div>
                            {
                                validationErrors.includes('headline') && (
                                    <div className="flex items-center mt-2">
                                        <CircleAlert size={12} className='text-red-400' />
                                        <p className='text-xs ml-1 text-red-400'>Headline must be at least 10 characters.</p>
                                    </div>
                                )
                            }
                            {
                                validationErrors.includes('headlineWordLimit') && (
                                    <div className="flex items-center mt-2">
                                        <CircleAlert size={12} className='text-red-400' />
                                        <p className='text-xs ml-1 text-red-400'>Headline cannot exceed 20 words.</p>
                                    </div>
                                )
                            }
                            {
                                validationErrors.includes('headlineBad') && (
                                    <div className="flex items-center mt-2">
                                        <CircleAlert size={12} className='text-red-400' />
                                        <p className='text-xs ml-1 text-red-400'>
                                            Headline contains inappropriate words: {checkForBadWords(headline).join(', ')}
                                        </p>
                                    </div>
                                )
                            }
                        </div>
                        <span className={`text-xs ${headlineWordCount > 20 ? 'text-red-400' : 'text-gray-500'}`}>
                            {headlineWordCount}/20 words
                        </span>
                    </div>
                </div>
            </div>

            <div className='mt-3 pt-5'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                    <LabelWithAsterisk label='Description' className='text-black'/>
                    {aiSlot}
                </div>
                <div>
                    <Textarea
                    className={`mt-3 resize-y ${(validationErrors.includes('description') || validationErrors.includes('descriptionWordLimit') || validationErrors.includes('descriptionCharLimit')) && 'border-red-400'} h-[150px]`}
                    value={description}
                    placeholder={`Highlight the property's features and the appeal of its location.`}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={2000}
                    disabled={markAsDuplicate}/>
                </div>
                <div className="flex justify-between items-center mt-1">
                    <div>
                        {
                            validationErrors.includes('description') && (
                                <div className="flex items-center mt-2">
                                    <CircleAlert size={12} className='text-red-400' />
                                    <p className='text-xs ml-1 text-red-400'>
                                        Description must be at least 20 words.
                                    </p>
                                </div>
                            )
                        }
                        {
                            validationErrors.includes('descriptionWordLimit') && (
                                <div className="flex items-center mt-2">
                                    <CircleAlert size={12} className='text-red-400' />
                                    <p className='text-xs ml-1 text-red-400'>
                                        Description cannot exceed 200 words.
                                    </p>
                                </div>
                            )
                        }
                        {
                            validationErrors.includes('descriptionCharLimit') && (
                                <div className="flex items-center mt-2">
                                    <CircleAlert size={12} className='text-red-400' />
                                    <p className='text-xs ml-1 text-red-400'>
                                        Description cannot exceed 2000 characters.
                                    </p>
                                </div>
                            )
                        }
                        {
                            validationErrors.includes('descriptionBad') && (
                                <div className="flex items-center mt-2">
                                    <CircleAlert size={12} className='text-red-400' />
                                    <p className='text-xs ml-1 text-red-400'>
                                        Description contains inappropriate words: {checkForBadWords(description).join(', ')}
                                    </p>
                                </div>
                            )
                        }
                    </div>
                    <span className={`text-xs ${descriptionWordCount > 200 ? 'text-red-400' : 'text-gray-500'}`}>
                        {descriptionWordCount}/200 words
                    </span>
                </div>
            </div>
        </section>
    );
});

export default DescriptionDetails;